import type { ExpressionValue } from 'ol/expr/expression'
import type { Style } from 'ol/layer/WebGLTile'

import type { LegendOverrides, LegendState } from '../legend-editor/useUserFacingLegend'

type Operator = '+' | '-' | '*' | '/' | '==' | '<' | '>' | '!=' | '<=' | '>='
type OperationResult = [Operator, ExpressionValue, ExpressionValue]

type StyleConfig = LegendState & {
  overrides: LegendOverrides
}

export const HIGHLIGHT_COLOR = 'rgb(255,100,0)'
export const NON_HIGHLIGHT_COLOR = 'rgba(0,0,0,0.5)'
export const DEEMPHASIZED_OPACITY = 0.4

/**
 * Hard cap on the number of distinct filter layers that can be applied to tile
 * rendering at once. Each slot consumes 4 bands in the composite tile (primary
 * bands 1-4 + 4 bands per slot), so the ceiling is set by WebGL texture limits.
 */
export const NUM_FILTER_SLOTS = 3

/**
 * Per-slot cap on discrete values (blues) that can be OR'd together as a single
 * multi-select filter. 8 covers every realistic discrete legend the product has
 * shown (crop hybrids, implement types, etc.) with room to spare.
 */
export const MAX_DISCRETE_BLUES_PER_SLOT = 8

/**
 * Include vs. exclude semantics for a filter slot.
 * Include: only show pixels that match the filter (mask non-matching).
 * Exclude: hide pixels that match the filter (mask matching).
 */
export enum FilterType {
  Include = 0,
  Exclude = 1
}

const op =
  (operator: Operator) =>
  (x: ExpressionValue, y: ExpressionValue): OperationResult => [operator, x, y]

const sum = op('+')
const product = op('*')
const quotient = op('/')
const difference = op('-')
const equals = op('==')
const notEquals = op('!=')
const gt = op('>')
const lt = op('<')
const gte = op('>=')
const lte = op('<=')
const round = (x: ExpressionValue) => ['round', x]
const ceil = (x: ExpressionValue) => ['ceil', x]

const all = (args: ExpressionValue[]) => ['all', ...args]
const any = (args: ExpressionValue[]) => ['any', ...args]

/** access value in specified band */
const band = (x: number) => ['band', x]

/** access value of variable by name */
const variable = (name: string) => ['var', name]

const rgba = (r: number, g: number, b: number, a: number) => {
  return ['color', r, g, b, a]
}

const hasNoData = all([
  equals(variable('shader'), 'data'),
  equals(band(1), 0),
  equals(band(2), 0),
  equals(band(3), 0)
])

/**
 * The adjustment factor is a correction mechanism for discrepancies between
 * real-time data from the sensor and after-the-fact data from the user.
 * Specifically, in harvest, the user can re-measure the weight of the harvest
 * with a scale, and that weight can be used to adjust the data we've already
 * stored from the sensor. The map tiles represent raw sensor data, so we need
 * to apply the adjustment factor here in the shader. In the 20|20 world, what
 * we're calling `adjustmentFactor` here is called the `k value`, and reported by
 * stitch as the `gain`. We sometimes use `gain` to mean something different
 * in the context of legend editing, where the gain and offset are used to scale
 * and shift legend colors as the user adjusts the legend.
 */
const normalizedValueAtPixel: ExpressionValue = product(
  sum(
    difference(1, band(1)),
    sum(quotient(difference(1, band(2)), 255), quotient(quotient(difference(1, band(3)), 255), 255))
  ),
  variable('adjustmentFactor')
)

/**
 * Slot 0 → bands 5-8, slot 1 → bands 9-12, slot 2 → bands 13-16.
 * Primary tile occupies bands 1-4.
 */
const slotBandOffset = (slot: number) => 4 + 4 * slot

/**
 * Normalized pixel value for a filter slot's secondary tile. Each slot uses
 * its own adjustment factor since each is a different layer with different gain.
 */
const slotNormalizedValueAtPixel = (slot: number): ExpressionValue => {
  const offset = slotBandOffset(slot)
  return product(
    sum(
      difference(1, band(1 + offset)),
      sum(
        quotient(difference(1, band(2 + offset)), 255),
        quotient(quotient(difference(1, band(3 + offset)), 255), 255)
      )
    ),
    variable(`filter${slot}_adjustmentFactor`)
  )
}

/**
 * Normalize a value in slot N's metric units into [0, 1] using that slot's
 * defined range. Allows comparing user-provided filter bounds (in the filter
 * layer's native units) against the normalized pixel value from that slot.
 */
const normalizeSlotValue = (slot: number, value: ExpressionValue) =>
  quotient(
    difference(value, variable(`filter${slot}_rangeMin`)),
    difference(variable(`filter${slot}_rangeMax`), variable(`filter${slot}_rangeMin`))
  )

const blendPassLine = (colorCases: ExpressionValue) => [
  'interpolate',
  ['linear'],
  band(4),
  0,
  colorCases,
  1,
  rgba(0, 0, 0, 1)
]

const denormalized = (band: ExpressionValue) => product(band, 255)

const isDiscreteHighlight = gt(variable('highlightBlue'), 0)

const isHighlightedBlue = equals(denormalized(band(3)), variable('highlightBlue'))

const isNotHighlightedBlue = notEquals(denormalized(band(3)), variable('highlightBlue'))

const shouldHighlight: [ExpressionValue, ExpressionValue] = [
  all([isDiscreteHighlight, isHighlightedBlue]),
  HIGHLIGHT_COLOR
]

const shouldUnHighlight: [ExpressionValue, ExpressionValue] = [
  all([isDiscreteHighlight, isNotHighlightedBlue, notEquals(band(3), 0)]),
  NON_HIGHLIGHT_COLOR
]

const discreteHighlightCases: [ExpressionValue, ExpressionValue][] = [
  shouldHighlight,
  shouldUnHighlight
]

const color = {
  blank: rgba(0, 0, 0, 0)
}

/**
 * Build the reject-cases for one filter slot. Each returned [condition, color]
 * pair masks a pixel to NON_HIGHLIGHT_COLOR when its filter condition fails.
 * Cases are evaluated first-match, so one slot's rejection short-circuits the
 * rest. All branches are guarded by `filter${slot}_active` so inactive slots
 * contribute nothing.
 *
 * `slotNormalizedValueAtPixel` already returns a value in [0,1] (fracPos * gain).
 * `normalizeSlotValue` maps a user-provided bound from metric units into the same
 * [0,1] space: (value - rangeMin) / (rangeMax - rangeMin). Comparing these two
 * directly is correct — do NOT wrap `slotNormalizedValueAtPixel` in a second
 * `normalizeSlotValue` call, which would divide by the metric span a second time
 * and collapse all pixel values to ≈0.
 */
const filterCasesForSlot = (slot: number): [ExpressionValue, ExpressionValue][] => {
  const active = equals(variable(`filter${slot}_active`), 1)
  const isDiscrete = equals(variable(`filter${slot}_isDiscrete`), 1)
  const isContinuous = equals(variable(`filter${slot}_isDiscrete`), 0)
  const isInclude = equals(variable(`filter${slot}_type`), FilterType.Include)
  const isExclude = equals(variable(`filter${slot}_type`), FilterType.Exclude)

  const bandOffset = slotBandOffset(slot)
  const pixelBlue = round(denormalized(band(3 + bandOffset)))

  const normalizedPixel = slotNormalizedValueAtPixel(slot)
  const normalizedMin = normalizeSlotValue(slot, variable(`filter${slot}_continuousMin`))
  const normalizedMax = normalizeSlotValue(slot, variable(`filter${slot}_continuousMax`))

  // Multi-select discrete: up to MAX_DISCRETE_BLUES_PER_SLOT uniforms, each
  // holding either a valid blue (0–255) or the -1 sentinel for "unused".
  // Sentinel slots never match a real pixel blue, so they harmlessly contribute
  // to the AND (include) or OR (exclude) without affecting the result.
  const blueUniforms = Array.from({ length: MAX_DISCRETE_BLUES_PER_SLOT }, (_, i) =>
    variable(`filter${slot}_blue${i}`)
  )
  const pixelMatchesNoBlue = all(blueUniforms.map((b) => notEquals(pixelBlue, b)))
  const pixelMatchesAnyBlue = any(blueUniforms.map((b) => equals(pixelBlue, b)))

  return [
    // Continuous include: pixel value outside [min, max] → mask
    [
      all([active, isContinuous, isInclude, lt(normalizedPixel, normalizedMin)]),
      NON_HIGHLIGHT_COLOR
    ],
    [
      all([active, isContinuous, isInclude, gt(normalizedPixel, normalizedMax)]),
      NON_HIGHLIGHT_COLOR
    ],
    // Continuous exclude: pixel value inside [min, max] → mask
    [
      all([
        active,
        isContinuous,
        isExclude,
        gte(normalizedPixel, normalizedMin),
        lte(normalizedPixel, normalizedMax)
      ]),
      NON_HIGHLIGHT_COLOR
    ],
    // Discrete include: pixel blue matches none of the selected → mask
    [all([active, isDiscrete, isInclude, pixelMatchesNoBlue]), NON_HIGHLIGHT_COLOR],
    // Discrete exclude: pixel blue matches any of the selected → mask
    [all([active, isDiscrete, isExclude, pixelMatchesAnyBlue]), NON_HIGHLIGHT_COLOR]
  ]
}

const defaultSlotVariables = (slot: number): Record<string, number> => ({
  [`filter${slot}_active`]: 0,
  [`filter${slot}_isDiscrete`]: 0,
  [`filter${slot}_type`]: FilterType.Include,
  [`filter${slot}_continuousMin`]: 0,
  [`filter${slot}_continuousMax`]: 0,
  [`filter${slot}_rangeMin`]: 0,
  [`filter${slot}_rangeMax`]: 1,
  [`filter${slot}_adjustmentFactor`]: 1,
  ...Object.fromEntries(
    Array.from({ length: MAX_DISCRETE_BLUES_PER_SLOT }, (_, i) => [`filter${slot}_blue${i}`, -1])
  )
})

// Discrete layers arrive with `definedRange = { low: NaN, high: NaN }` and
// `initialRange` likewise NaN. GLSL arithmetic on NaN propagates — expressions
// like `(value - rangeMin) / (rangeMax - rangeMin)` produce NaN that leaks into
// the fragment output and corrupts it enough that OL silently stops drawing tiles
// (observable as the satellite base layer going blank). Coerce non-finite values
// to 0 before sending to the GPU so all uniforms are well-defined.
const safe = (value: number): number => (Number.isFinite(value) ? value : 0)

/**
 * Per-slot uniform updates for a single filter slot. Pass the result directly
 * to WebGLTile.updateStyleVariables. Pass null to deactivate the slot.
 */
export type FilterSlotUniforms = {
  isDiscrete: boolean
  type: FilterType
  continuousMin: number
  continuousMax: number
  rangeMin: number
  rangeMax: number
  adjustmentFactor: number
  /** Up to MAX_DISCRETE_BLUES_PER_SLOT values; extras are ignored. */
  blues: number[]
}

/**
 * Produce the style-variable object for a filter slot. Pass null to emit
 * inactive defaults that deactivate the slot without masking any pixels.
 */
export const filterSlotVariables = (
  slot: number,
  uniforms: FilterSlotUniforms | null
): Record<string, number> => {
  if (uniforms === null) return defaultSlotVariables(slot)
  const blues = uniforms.blues.slice(0, MAX_DISCRETE_BLUES_PER_SLOT)
  const bluePadding = MAX_DISCRETE_BLUES_PER_SLOT - blues.length
  const rangeMin = safe(uniforms.rangeMin)
  const safeRangeMax = safe(uniforms.rangeMax)
  // Guard against division by zero in normalizeSlotValue: if safe() collapses both
  // bounds to the same value (e.g. NaN,NaN → 0,0), nudge rangeMax off rangeMin.
  const rangeMax = safeRangeMax === rangeMin ? rangeMin + Number.EPSILON : safeRangeMax
  return {
    [`filter${slot}_active`]: 1,
    [`filter${slot}_isDiscrete`]: uniforms.isDiscrete ? 1 : 0,
    [`filter${slot}_type`]: uniforms.type,
    [`filter${slot}_continuousMin`]: safe(uniforms.continuousMin),
    [`filter${slot}_continuousMax`]: safe(uniforms.continuousMax),
    [`filter${slot}_rangeMin`]: rangeMin,
    [`filter${slot}_rangeMax`]: rangeMax,
    [`filter${slot}_adjustmentFactor`]: safe(uniforms.adjustmentFactor),
    ...Object.fromEntries(blues.map((b, i) => [`filter${slot}_blue${i}`, b])),
    ...Object.fromEntries(
      Array.from({ length: bluePadding }, (_, i) => [`filter${slot}_blue${blues.length + i}`, -1])
    )
  }
}

export const style = (config: StyleConfig, recordIdBandOffset: number | null): Style => {
  const discretePalette = config.discreteColors
  const continuousGradient = config.normalizedGradient

  const discreteCases = discretePalette.map((p): [ExpressionValue, ExpressionValue] => [
    all([equals(band(1), 0), gt(band(3), 0), equals(round(denormalized(band(3))), p.blue)]),
    p.color
  ])

  const boundaryCases: [ExpressionValue, ExpressionValue][] = [
    [
      all([
        equals(variable('shader'), 'boundary'),
        equals(variable('negated'), 0),
        equals(band(3), 0)
      ]),
      NON_HIGHLIGHT_COLOR
    ],
    [
      all([
        equals(variable('shader'), 'boundary'),
        equals(variable('negated'), 1),
        equals(band(3), 0)
      ]),
      NON_HIGHLIGHT_COLOR
    ]
  ]

  /**
   * Normalize a value to the range [0, 1] given the absolute range [absMin, absMax]
   */
  const normalizeUserValue = (value: ExpressionValue) =>
    quotient(
      difference(value, variable('rangeMin')),
      difference(variable('rangeMax'), variable('rangeMin'))
    )

  /**
   * The user-defined value, normalized to the range [0, 1],
   * derived from the absolute range [absMin, absMax]
   */
  const normalizedUserMin = normalizeUserValue(variable('userMin'))

  /**
   * The user-defined maximum value, normalized to the range [0, 1],
   * derived from the absolute range [absMin, absMax]
   */
  const normalizedUserMax = normalizeUserValue(variable('userMax'))

  /**
   * The size of each step in the gradient, within the user-defined range
   * after normalization.
   *
   * Dividing by (stepCount - 2) creates (stepCount - 2) equal steps within the
   * user range, plus one step for values below userMin and one for values above userMax.
   *
   * Example: range [0.2, 0.4] with 3 steps
   * - stepSize = 0.2 / (3-2) = 0.2
   * - Step 0: <0.2 (below user range)
   * - Step 1: 0.2-0.4 (within user range)
   * - Step 2: >0.4 (above user range)
   */
  const stepSize = quotient(
    difference(normalizedUserMax, normalizedUserMin),
    difference(variable('stepCount'), 2)
  )

  /**
   * The index of the bin that the value falls into in an array of bins/colors
   */
  const stepIndex = ceil(quotient(difference(normalizedValueAtPixel, normalizedUserMin), stepSize))

  /**
   * The discretized, representative value for the bin. All pixel values within a bin
   * get mapped to the same binned value, which is then used to look up the color
   * from the gradient.
   */
  const discretizedValue = sum(normalizedUserMin, product(stepIndex, stepSize))

  /**
   * Continuous color interpolation with discretization alignment
   *
   * This complex scaling serves to align discretized bin values with gradient stops.
   * Here's how it works:
   *
   * 1. DISCRETIZATION SCHEME:
   *    We divide the data into `stepCount` bins total:
   *    - 1 bin for values BELOW userMin (bin 0)
   *    - (stepCount - 2) equal bins WITHIN [userMin, userMax] (bins 1 to stepCount-2)
   *    - 1 bin for values ABOVE userMax (bin stepCount-1)
   *
   *    This is why stepSize = (userMax - userMin) / (stepCount - 2)
   *
   * 2. DISCRETIZED VALUES:
   *    Each bin gets mapped to a representative value:
   *    - Bin 0: userMin (or below)
   *    - Bin 1: userMin + stepSize
   *    - Bin 2: userMin + 2*stepSize
   *    - ...
   *    - Bin (stepCount-2): userMin + (stepCount-2)*stepSize = userMax
   *    - Bin (stepCount-1): userMax (or above)
   *
   *    So we have (stepCount-1) distinct discretized values that span
   *    from userMin to userMax.
   *
   * 3. GRADIENT STOP POSITIONS:
   *    The continuousGradient has stops in [0, 1] that need to map to our
   *    discretized values. We want:
   *    - Gradient stop 0.0 → maps to userMin (first discretized value)
   *    - Gradient stop 1.0 → maps to userMax (last discretized value)
   *    - Intermediate stops → map to intermediate discretized values
   *
   * 4. THE SCALING FACTOR:
   *    The factor (stepCount-1)/(stepCount-2) ensures proper alignment:
   *
   *    - We have (stepCount-1) discretized values spanning the range
   *    - But only (stepCount-2) equal steps within that range
   *    - This ratio scales the gradient stops so they align with our
   *      discretized values, not with the bin boundaries
   *    - If we did not do this, the gradient colors would fall between the
   *      discretized values, leading to incorrect color mapping.
   *
   *    Example with stepCount=4:
   *    - 3 discretized values at: userMin, userMin+stepSize, userMax
   *    - 2 steps within the range
   *    - Scaling factor = 3/2 = 1.5
   *    - Gradient stops [0, 0.5, 1] become [0, 0.75, 1.5] after scaling
   *    - This ensures gradient colors align with discretized values
   */
  const continuousColor = [
    'interpolate',
    ['linear'],
    discretizedValue, // The discretized value to look up in the gradient
    ...continuousGradient.flatMap(({ stop, color }) => [
      // Transform gradient stop position to align with discretized values:
      // 1. Scale stop from [0,1] to user range width
      // 2. Apply discretization alignment factor
      // 3. Shift by userMin to get final position
      sum(
        product(
          product(stop, difference(normalizedUserMax, normalizedUserMin)),
          quotient(
            difference(variable('stepCount'), 1), // Number of discretized values
            difference(variable('stepCount'), 2) // Number of steps in user range
          )
        ),
        normalizedUserMin
      ),
      color
    ])
  ]

  const isHighlightMode = equals(variable('isHighlightMode'), Number(true))

  const continuousHighlightCases: [ExpressionValue, ExpressionValue][] = [
    [
      all([isHighlightMode, lt(normalizedValueAtPixel, normalizeUserValue(variable('userMin')))]),
      NON_HIGHLIGHT_COLOR
    ],
    [
      all([isHighlightMode, lt(normalizedValueAtPixel, normalizeUserValue(variable('userMax')))]),
      HIGHLIGHT_COLOR
    ],
    [
      all([
        isHighlightMode,
        any([gt(band(1), 0), gt(band(2), 0)]) // check if any continuous data present at all
      ]),
      NON_HIGHLIGHT_COLOR
    ]
  ]

  // Multi-slot filter: up to NUM_FILTER_SLOTS filter layers each with its own
  // secondary tile in bands [5+4N..8+4N]. Cases from all slots are concatenated;
  // first match wins, so any one slot's rejection masks the pixel. Inactive slots
  // (filter${slot}_active = 0) contribute no cases.
  const filterCases = Array.from({ length: NUM_FILTER_SLOTS }).flatMap((_, slot) =>
    filterCasesForSlot(slot)
  )

  const cases = [
    [hasNoData, color.blank],
    ...boundaryCases,
    ...filterCases,
    ...discreteHighlightCases,
    ...discreteCases,
    ...continuousHighlightCases
  ]

  /**
   * The fallback color is the continuous color, if it exists, or the blank color
   * if there are no continuous stops in the gradient.
   */
  const fallback = continuousGradient.length > 0 ? continuousColor : color.blank

  const { overrides } = config

  const perSlotDefaults = Array.from({ length: NUM_FILTER_SLOTS }).reduce<Record<string, number>>(
    (acc, _, slot) => Object.assign(acc, defaultSlotVariables(slot)),
    {}
  )

  const dataColor = blendPassLine(['case', ...cases.flat(), fallback])

  return {
    color:
      recordIdBandOffset === null
        ? dataColor
        : [
            'case',
            gt(recordIdAtBand(recordIdBandOffset), variable('lastAppliedRecordId')),
            product(dataColor, rgba(255, 255, 255, DEEMPHASIZED_OPACITY)),
            dataColor
          ],
    variables: {
      rangeMin: safe(config.definedRange.low),
      rangeMax: safe(config.definedRange.high),
      highlightBlue: 0,
      isHighlightMode: Number(overrides.isHighlightMode ?? false),
      adjustmentFactor: config.adjustmentFactor ?? 1,
      negated: Number(config.negated === true),
      stepCount: overrides.continuousBinCount ?? config.continuousBinCount,
      shader: config.shader,
      userMin: safe(overrides.activeRange?.low ?? config.initialRange.low),
      userMax: safe(overrides.activeRange?.high ?? config.initialRange.high),
      lastAppliedRecordId: MAX_RECORD_ID,
      ...perSlotDefaults
    }
  }
}

/**
 * Either flavor of byte-typed array OL's `getData` may return depending on
 * whether the tile is single-band (Uint8Array, from fast-png decode) or
 * multi-band composite (Uint8ClampedArray, from the secondary stacking
 * loader). Both index identically; the distinction matters only for
 * `instanceof` checks.
 */
export type RawPixel = Uint8Array | Uint8ClampedArray

/**
 * Check if the pixel represents a discrete data point
 */
export const isDiscretePixel = (pixel: RawPixel) => {
  return pixel[0] === 0 && pixel[2] > 0
}

/**
 * Get the continuous value at the pixel, given the absolute range and adjustment factor.
 *
 * This duplicates the logic in the shader, so if the shader changes, this function should be updated.
 * It has to be separate, because this is used in typescript, whereas the shader is used in GLSL.
 */
export const getContinuousValueAtPixel = (
  rawPixel: RawPixel,
  range: { low: number; high: number },
  adjustmentFactor: number
) => {
  const [r, g, b] = [...rawPixel].map((x) => x / 255)
  const normalizedValue = 1 - r + (1 - g) / 255 + (1 - b) / (255 * 255)
  return (normalizedValue * (range.high - range.low) + range.low) * adjustmentFactor
}

/**
 * Check if the pixel has data in it, i.e., it's not a pass line, an empty pixel,
 * or in some unexpected data format (we require RGB pixels). Accepts either
 * a `Uint8Array` (single-band path) or a `Uint8ClampedArray` (the multi-band
 * composite loader for filter slots) — without that, hover silently bails
 * the moment a filter row exists.
 */
export const isPixelWithData = (pixel: unknown): pixel is RawPixel => {
  return (
    (pixel instanceof Uint8Array || pixel instanceof Uint8ClampedArray) &&
    pixel.slice(0, 3).some((d) => d !== 0)
  )
}
export type RecordRange = { first: number; last: number }

export type RecordRangeStyleConfig = {
  /**
   * One entry per kept output range, in slot order. Colors are baked into the compiled expression
   * as literals since they rarely change; the numeric bounds are variables so they can update
   * without recompiling.
   */
  ranges: (RecordRange & { color: string })[]
  /**
   * Shown for any record id outside every range (gaps, trimmed leading/trailing records).
   */
  trimmedColor: string
}

/**
 * Mirrors MAX_DISCRETE_BLUES_PER_SLOT's headroom reasoning — real splits are 2-6 outputs.
 */
export const MAX_RECORD_RANGES = 8

/**
 * Decodes the 24-bit record id packed into the record-id layer's RGB channels (R + G*256 + B*256^2).
 * Record id 0 is indistinguishable from "no coverage" (all channels zero), so it renders as such.
 */
const recordIdAtBand = (offset: number): ExpressionValue =>
  sum(
    round(denormalized(band(1 + offset))),
    sum(
      product(round(denormalized(band(2 + offset))), 256),
      product(round(denormalized(band(3 + offset))), 256 * 256)
    )
  )

const recordIdAtPixel = recordIdAtBand(0)

/**
 * The maximum *mappable* record id, in a 3-channel rgb-encoded
 * record map layer.
 */
export const MAX_RECORD_ID = 256 ** 3 - 1

const hasNoRecordData = equals(recordIdAtPixel, 0)

const isInRecordRange = (slot: number) =>
  all([
    gte(recordIdAtPixel, variable(`recordRange${slot}_first`)),
    lte(recordIdAtPixel, variable(`recordRange${slot}_last`))
  ])

/**
 * The style variables for a set of ranges, capped at `MAX_RECORD_RANGES`. The single source of the
 * uniform naming scheme, so everything that declares or updates these variables stays in agreement.
 */
export const recordRangeVariables = (ranges: RecordRange[]): Record<string, number> =>
  Object.fromEntries(
    ranges.slice(0, MAX_RECORD_RANGES).flatMap((range, slot) => [
      [`recordRange${slot}_first`, range.first],
      [`recordRange${slot}_last`, range.last]
    ])
  )

/**
 * Shader for the stream trim/split editing map layer: highlights, by pixel, which kept range (if
 * any) a record id falls into. Not agronomic-legend based, unlike `style()`.
 */
export const recordLayerStyle = (config: RecordRangeStyleConfig): Style => {
  const ranges = config.ranges.slice(0, MAX_RECORD_RANGES)

  const rangeCases: [ExpressionValue, ExpressionValue][] = ranges.map((range, slot) => [
    isInRecordRange(slot),
    range.color
  ])

  return {
    color: ['case', hasNoRecordData, color.blank, ...rangeCases.flat(), config.trimmedColor],
    variables: recordRangeVariables(ranges)
  }
}
